import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PENDING_SESSION_SWITCH_KEY } from '@/features/account-switcher';
import { useReauthModal } from '@/features/auth';
import { isAuthRejection, isExpiredSessionRejection, isSessionRejection, type useAuthQuery } from '@/services/auth';
import type { AugmentedUser } from '@/services/auth/types';

type UseReauthPromptArgs = {
  /** Only the rejection and the retained last-good data are read. */
  auth: Pick<ReturnType<typeof useAuthQuery>, 'error' | 'data'>;
  /** The shared session latch (`session.isAuthenticated`). */
  isSessionAuthenticated: boolean;
  /** The last-good workspace/environment id, for scoping the analytics event. */
  currentWorkspaceId: number | undefined;
};

type ReauthPrompt = {
  /**
   * A rejection the layout must act on now (open the modal, or route to login /
   * workspace-mismatch recovery). Excludes stale stragglers whose session is
   * still live. The render path branches on this.
   */
  hasLiveSessionRejection: boolean;
  /**
   * The last-good user to re-authenticate as, when an *expired session* (401)
   * left one loaded; `null` otherwise (fresh tab, masq, or a straggler). The
   * render path uses it to decide modal-vs-`/login`.
   */
  rejectedUser: AugmentedUser | null;
};

/**
 * Owns the authenticated layout's in-place re-auth trigger: on a session
 * rejection, keep the app mounted from the retained last-good data and
 * re-authenticate through the managed modal instead of redirecting to /login and
 * losing work. The modal opens only when a user was previously loaded — tabs
 * with no user context (or masq rejections) fall through to the layout's login
 * redirect. Recovery re-resolves the auth query on success, which clears the
 * rejection and trips this effect's cleanup to close the modal.
 *
 * Returns the two derivations the layout's render path also needs, so the
 * recovery branches and this side effect stay in sync.
 */
export function useReauthPrompt({
  auth,
  isSessionAuthenticated,
  currentWorkspaceId,
}: UseReauthPromptArgs): ReauthPrompt {
  // Only an *expired* session (401) gets the in-place modal. A 403 (workspace
  // off-limits, expired masquerade) can't be fixed by re-entering credentials,
  // so it falls through to the login redirect / workspace-mismatch recovery.
  const rejectedAccount =
    isExpiredSessionRejection(auth.error) && auth.data?.account != null ? auth.data.account : null;

  // A *stale straggler* is a session-scoped rejection whose latch flip the
  // auth-epoch guard skipped because a fresh re-auth already landed — the queryFn
  // still throws it, but the session is live (`isSessionAuthenticated`). Treating
  // that as a live rejection would open the modal and bounce the valid session to
  // /login, and that redirect oscillates with the recovery refetch, looping
  // `NavigationHistoryProvider` into a crash. Ignore only those.
  //
  // Masq rejections are NOT stragglers: they keep the store authenticated because
  // `isSessionRejection` excludes masq (the admin's own session is fine), yet a
  // bad masq target must still route to /login — so they stay live rejections.
  const isStaleStraggler = isSessionRejection(auth.error) && isSessionAuthenticated;
  const hasLiveSessionRejection = isAuthRejection(auth.error) && !isStaleStraggler;
  const rejectedUser = rejectedAccount != null && !isStaleStraggler ? (auth.data?.user ?? null) : null;

  // A suppressed straggler still leaves the auth query rejected while the
  // session is live. When this tab's modal is up, its own recovery effect
  // re-resolves the query — but when the re-auth landed before the modal ever
  // mounted here (another tab won the race), nothing else does, and the query
  // would sit on the stale rejection until an unrelated refetch trigger.
  // Re-resolve it so the rejection clears and the retained data refreshes.
  const queryClient = useQueryClient();
  const authError = auth.error;
  useEffect(() => {
    if (isSessionRejection(authError) && isSessionAuthenticated) {
      void queryClient.invalidateQueries({ queryKey: ['authenticated'] });
    }
  }, [authError, isSessionAuthenticated, queryClient]);

  const rejectedUserId = rejectedUser?.id;
  const rejectedUserEmail = rejectedUser?.email;
  const rejectedUserProvider = rejectedUser?.external_identity_provider;
  // Route password-vs-SSO the way Ember does: `account.sso_setting === 'required'`
  // (or a set external identity provider) means the user has no password form.
  const rejectedSsoRequired = rejectedAccount?.sso_setting === 'required';
  // The last-good environment scopes the modal's `re_authenticated` event (Ember
  // tracks it on the current environment). Same source `useAnalytics` uses.
  const reauthEnvironmentId = currentWorkspaceId != null ? String(currentWorkspaceId) : null;

  const reauthModal = useReauthModal();
  useEffect(() => {
    if (rejectedUserId == null || rejectedUserEmail == null) {
      return;
    }
    // A failed multi-account switch owns this rejection: `handleExpiredSessionFallback`
    // reloads into a fallback session (or redirects to login). Don't also open the
    // modal — it's rendered at the app shell, so it would flash over that reload.
    if (sessionStorage.getItem(PENDING_SESSION_SWITCH_KEY) != null) {
      return;
    }
    reauthModal.open({
      user: {
        id: rejectedUserId,
        email: rejectedUserEmail,
        externalIdentityProvider: rejectedUserProvider ?? '',
        ssoRequired: rejectedSsoRequired,
      },
      environmentId: reauthEnvironmentId,
    });
    return () => reauthModal.closeAllOfType();
  }, [rejectedUserId, rejectedUserEmail, rejectedUserProvider, rejectedSsoRequired, reauthEnvironmentId, reauthModal]);

  return { hasLiveSessionRejection, rejectedUser };
}
